import { Diagnostics } from '../components/Diagnostics';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { HelpHint } from '../components/HelpHint';
import { PanelError } from '../components/PanelError';
import { TableSkeleton } from '../components/Skeleton';
import { api } from '../lib/api';

function Dot({ ok }: { ok: boolean }) {
  return ok ? (
    <CheckCircle2 className="h-5 w-5 text-secondary-500" aria-hidden="true" />
  ) : (
    <XCircle className="h-5 w-5 text-danger-500" aria-hidden="true" />
  );
}

export function StatusPage() {
  const health = useQuery({
    queryKey: ['readiness'],
    queryFn: api.readiness,
    refetchInterval: 10_000, // ARCHITECTURE.md §2 — polling beats websockets at this volume
  });

  const info = useQuery({ queryKey: ['info'], queryFn: api.info, retry: false });

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h2 className="font-heading text-2xl">System status</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Live check of everything the dispatch system depends on. Refreshes every 10 seconds.
        </p>
      </div>

      {/*
        First, above the dependency list. Somebody opens this page because
        something is wrong, and the answer is more often "email is set to mock"
        than "Redis is down" — so the checks that name a fix come before the
        ones that report a connection.
      */}
      <Diagnostics />

      <section className="card">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-1.5 font-heading text-lg">
            Dependencies
            <HelpHint>
              Readiness means Postgres and Redis are usable. If either is down the API returns 503
              and stops taking traffic, but does not restart itself.
            </HelpHint>
          </h3>
          {health.isFetching && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
          )}
        </div>

        {/*
          Shaped like the answer, so the panel does not jump when it arrives.
          "Checking…" was one line where four were coming.
        */}
        {health.isPending && <TableSkeleton columns={2} rows={3} label="Checking readiness" />}

        {health.isError && (
          <div className="flex items-start gap-3 rounded-xl bg-danger-50 p-4 text-sm">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-danger-500" aria-hidden="true" />
            <div>
              <p className="font-semibold text-danger-600">API unreachable</p>
              <p className="mt-1 text-muted-foreground">
                Start it with <code className="rounded bg-muted px-1">npm run dev:api</code>, or
                bring up the whole stack with{' '}
                <code className="rounded bg-muted px-1">docker compose up</code>.
              </p>
            </div>
          </div>
        )}

        {health.data?.dependencies && (
          <ul className="divide-y divide-border">
            {health.data.dependencies.map((dep) => (
              <li key={dep.name} className="flex items-center gap-3 py-3">
                <Dot ok={dep.ok} />
                <span className="font-medium capitalize">{dep.name}</span>
                <span className="ml-auto text-sm text-muted-foreground">
                  {dep.ok ? `${dep.latencyMs} ms` : (dep.detail ?? 'unavailable')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card">
        <h3 className="mb-4 flex items-center gap-1.5 font-heading text-lg">
          Providers
          <HelpHint>
            Mock providers let the whole system run — dispatch, acknowledge, complete — without a
            Meta account, an AI key or spending a single real message.
          </HelpHint>
        </h3>

        {info.data?.providers ? (
          <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
            {Object.entries(info.data.providers).map(([name, mode]) => (
              <div key={name}>
                <dt className="capitalize text-muted-foreground">{name}</dt>
                <dd className="mt-0.5">
                  <span
                    className={`badge ${
                      mode === 'mock' || mode === 'disabled'
                        ? 'bg-muted text-muted-foreground'
                        : 'bg-secondary-50 text-secondary-700'
                    }`}
                  >
                    {mode}
                  </span>
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          /*
            "Unavailable." was the whole message. It names nothing, explains
            nothing and offers nothing — and this panel is the one somebody
            opens *because* something is already wrong.
          */
          <PanelError
            what="The provider list could not be read."
            cause="Usually the API is restarting, or this session has expired while the page sat open."
            fix="Try again. If it keeps failing, check readiness above — if that is red, this will stay red until it clears."
            detail={info.isError ? (info.error as Error)?.message : undefined}
            onRetry={() => void info.refetch()}
            retrying={info.isFetching}
          />
        )}

        {info.data && (
          <p className="mt-4 border-t border-border pt-4 text-xs text-muted-foreground">
            {info.data.orgName} · {info.data.timezone} · {info.data.environment} · v
            {info.data.version}
          </p>
        )}
      </section>
    </div>
  );
}
